import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Style.module.css';
import { getPaymentDetails } from '../../APICalling/APICalling';
import { useGlobalContext } from '../../Context/GlobalContext';
import LockIcon from '../Icons/LockIcon';
import menuIcon from '../../Assets/images/menu.svg';
import { MembershipTxnPaymentDetailSheet } from '../MembershipTransactionSidePanel/MembershipTransactionSidePanel';
import logger from '../../Utils/logger';

const getPaymentTransactionId = (payment) =>
  payment?.transaction_id ?? payment?.id ?? payment?.payment_transaction_id ?? payment?.payment_id ?? null;

const parseAmount = (value) => {
  const amount = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(amount) ? amount : null;
};

const formatAmount = (value) => Number(value).toFixed(2);

const isRefundPaymentRow = (payment) =>
  String(payment?.result_code ?? '').trim().toLowerCase() === 'refund requested';

const getParentTransactionId = (payment) => {
  const parentId = payment?.parent_transaction_id ?? payment?.parent_payment_transaction_id ?? null;
  if (parentId == null || String(parentId).trim() === '') return null;
  return String(parentId);
};

/** Group each payment with its refund row(s) via parent_transaction_id; preserve original payment order. */
const sortPaymentsWithRefunds = (payments, allPaymentsForOrder = payments) => {
  if (!Array.isArray(payments) || payments.length <= 1) return payments;

  const parentOrder = new Map();
  allPaymentsForOrder.forEach((payment, index) => {
    const txnId = getPaymentTransactionId(payment);
    if (txnId != null && !isRefundPaymentRow(payment)) {
      parentOrder.set(String(txnId), index);
    }
  });

  const refundRowsByParentId = new Map();
  const paymentRows = [];
  const orphanRefunds = [];

  payments.forEach((payment, index) => {
    const parentTxnId = getParentTransactionId(payment);
    if (isRefundPaymentRow(payment) && parentTxnId) {
      if (!refundRowsByParentId.has(parentTxnId)) {
        refundRowsByParentId.set(parentTxnId, []);
      }
      refundRowsByParentId.get(parentTxnId).push({ payment, index });
      return;
    }
    if (isRefundPaymentRow(payment)) {
      orphanRefunds.push({ payment, index });
      return;
    }
    paymentRows.push({
      payment,
      index,
      txnId: getPaymentTransactionId(payment) != null ? String(getPaymentTransactionId(payment)) : null,
    });
  });

  const sorted = [];
  const attachedRefundParents = new Set();

  paymentRows.sort((a, b) => a.index - b.index);
  paymentRows.forEach(({ payment, txnId }) => {
    sorted.push(payment);
    if (!txnId) return;
    const refunds = refundRowsByParentId.get(txnId) || [];
    refunds.sort((a, b) => a.index - b.index);
    refunds.forEach(({ payment: refundPayment }) => sorted.push(refundPayment));
    if (refunds.length > 0) attachedRefundParents.add(txnId);
  });

  const unattachedRefunds = [];
  refundRowsByParentId.forEach((refunds, parentTxnId) => {
    if (attachedRefundParents.has(parentTxnId)) return;
    refunds.forEach((entry) => unattachedRefunds.push(entry));
  });
  orphanRefunds.forEach((entry) => unattachedRefunds.push(entry));

  unattachedRefunds.sort((a, b) => {
    const aParentOrder = getParentTransactionId(a.payment)
      ? (parentOrder.get(getParentTransactionId(a.payment)) ?? a.index)
      : a.index;
    const bParentOrder = getParentTransactionId(b.payment)
      ? (parentOrder.get(getParentTransactionId(b.payment)) ?? b.index)
      : b.index;
    if (aParentOrder !== bParentOrder) return aParentOrder - bParentOrder;
    return a.index - b.index;
  });
  unattachedRefunds.forEach(({ payment }) => sorted.push(payment));

  return sorted.length > 0 ? sorted : payments;
};

const findLinkedRefundPayment = (payment, allPayments) => {
  const parentTxnId = getPaymentTransactionId(payment);
  if (!parentTxnId || isRefundPaymentRow(payment) || !Array.isArray(allPayments)) return null;
  return (
    allPayments.find(
      (row) =>
        isRefundPaymentRow(row) &&
        getParentTransactionId(row) === String(parentTxnId)
    ) ?? null
  );
};

const BookingPaymentDetails = ({
  bookingID: bookingIDProp = null,
  saleId: saleIdProp = null,
  from = 'home',
  paymentDetailsFromPaymentPage,
  loadingFromPaymentPage,
  onPaymentDetailsChange,
  paymentQueryParams,
  showPaymentMenu,
  onPaymentViewDetails,
  onGoToSale,
  hideMembershipPaymentMeta = false,
  refundMode = false,
  showRefundPair = false,
  childSaleId = null,
  parentSaleId = null,
}) => {
  const { bookingDetails } = useGlobalContext();
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPaymentMenuKey, setOpenPaymentMenuKey] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [fetchedPaymentDetail, setFetchedPaymentDetail] = useState(null);
  const [isPaymentDetailOpen, setIsPaymentDetailOpen] = useState(false);
  const [paymentDetailLoading, setPaymentDetailLoading] = useState(false);
  const paymentMenuRef = useRef(null);

  const bookingID =
    from === 'sale-booking-info' ? (bookingIDProp || null) : (bookingIDProp ?? bookingDetails?.id);
  const isProductSale =
    bookingDetails?.booking_type === 'product' ||
    bookingDetails?.type === 'product';
  const resolvedPaymentQueryParams =
    paymentQueryParams || (isProductSale && bookingID ? { sale_id: bookingID } : undefined);

  const useParentData = loadingFromPaymentPage !== undefined;
  const displayDetails = useParentData ? (paymentDetailsFromPaymentPage ?? []) : paymentDetails;
  const displayLoading = useParentData ? loadingFromPaymentPage : loading;
  const showPaymentActions = showPaymentMenu !== false;
  const isSaleBookingInfoContext = from === 'sale-booking-info';
  const hasBackendRefundRows = displayDetails.some(isRefundPaymentRow);
  const useLegacyRefundPair = showRefundPair && !refundMode && !hasBackendRefundRows;
  const visibleDetails = (() => {
    if (!refundMode) return displayDetails;
    const refundRows = displayDetails.filter(isRefundPaymentRow);
    return refundRows.length > 0 ? refundRows : displayDetails;
  })();
  const sortedVisibleDetails = sortPaymentsWithRefunds(visibleDetails, displayDetails);

  const resolveRefundSaleContext = () => {
    const resolvedSaleId = saleIdProp ?? bookingDetails?.sale_id ?? null;
    if (isSaleBookingInfoContext) {
      return { resolvedChildSaleId: childSaleId ?? null,  resolvedParentSaleId: parentSaleId ?? null, currentSaleId: resolvedSaleId,};
    }
    return { resolvedChildSaleId: childSaleId ?? bookingDetails?.child_sale_id ?? null, resolvedParentSaleId: parentSaleId ?? bookingDetails?.parent_sale_id ?? null,currentSaleId: resolvedSaleId,};
  };

  const buildPaymentDetailOverlay = (payment, { isRefundRow = false } = {}) => {
    const { resolvedChildSaleId, resolvedParentSaleId, currentSaleId } = resolveRefundSaleContext();
    const paymentChildSaleId = payment?.child_sale_id ?? payment?.refund_sale_id ?? payment?.refunded_sale_id ?? null;
    const effectiveChildSaleId = resolvedChildSaleId ?? paymentChildSaleId ?? null;
    const effectiveParentSaleId = resolvedParentSaleId ?? payment?.parent_sale_id ?? null;
    const paymentIsRefundRow = isRefundRow || isRefundPaymentRow(payment);
    const hasRelatedRefundSale = Boolean(effectiveChildSaleId) || Boolean(effectiveParentSaleId) || hasBackendRefundRows;
    return {
      __isRefundRow: paymentIsRefundRow,
      __isRefundPayment: paymentIsRefundRow || refundMode || Boolean(effectiveParentSaleId),
      __hasRelatedRefundSale: hasRelatedRefundSale,
      __childSaleId: effectiveChildSaleId,
      __parentSaleId: effectiveParentSaleId,
      __currentSaleId: currentSaleId,
    };
  };

  const hasPaymentDetails = sortedVisibleDetails.length > 0;

  useEffect(() => {
    if (typeof onPaymentDetailsChange === 'function') {
      onPaymentDetailsChange(hasPaymentDetails);
    }
  }, [hasPaymentDetails, onPaymentDetailsChange]);

  useEffect(() => {
    if (!showPaymentActions) return undefined;
    const handleClickOutside = (event) => {
      if (paymentMenuRef.current && !paymentMenuRef.current.contains(event.target)) {
        setOpenPaymentMenuKey(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPaymentActions]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setPaymentDetails([]);
      if (!bookingID) return;
      const response = await getPaymentDetails(
        saleIdProp ?? (isSaleBookingInfoContext ? bookingID : bookingDetails?.sale_id) ?? null,
        resolvedPaymentQueryParams
      );
      const data = response.data.data;
      setPaymentDetails(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [bookingID, bookingDetails?.sale_id, isSaleBookingInfoContext, resolvedPaymentQueryParams, saleIdProp]);

  const loadPaymentTransactionDetail = useCallback((payment) => {
    const isRefundRow = isRefundPaymentRow(payment) || Boolean(payment?.__isRefundRow);
    const detailOverlay = buildPaymentDetailOverlay(payment, { isRefundRow });
    const linkedRefundPayment = findLinkedRefundPayment(payment, displayDetails);
    setFetchedPaymentDetail({
      ...payment,
      ...detailOverlay,
      ...(linkedRefundPayment ? { __linkedRefundPayment: linkedRefundPayment } : {}),
    });
    setPaymentDetailLoading(false);
  }, [refundMode, childSaleId, parentSaleId, bookingIDProp, saleIdProp, from, bookingDetails?.child_sale_id, bookingDetails?.parent_sale_id, bookingDetails?.sale_id, bookingID, hasBackendRefundRows, displayDetails]);

  useEffect(() => {
    if (!useParentData) {
      fetchData();
    }
  }, [bookingDetails?.id, bookingIDProp, from, useParentData, fetchData, childSaleId, parentSaleId]);

  const openPaymentDetailRef = useRef({ isOpen: false, payment: null });
  openPaymentDetailRef.current = {isOpen: isPaymentDetailOpen, payment: fetchedPaymentDetail || selectedPayment,};

  useEffect(() => {
    const handlePaymentsRefresh = async (event) => {
      if (event?.type === 'salesPaymentSuccess') {
        const eventSaleId = event?.detail?.saleId ?? event?.detail?.bookingId ?? event?.detail?.id;
        if (eventSaleId != null && Number(eventSaleId) !== Number(saleIdProp ?? bookingID)) return;
      }
      if (!useParentData) { await fetchData();}
      const { isOpen, payment } = openPaymentDetailRef.current;
      if (isOpen && payment) { loadPaymentTransactionDetail(payment); }
    };
    window.addEventListener('voidSaleCompleted', handlePaymentsRefresh);
    window.addEventListener('salesPaymentSuccess', handlePaymentsRefresh);
    return () => {
      window.removeEventListener('voidSaleCompleted', handlePaymentsRefresh);
      window.removeEventListener('salesPaymentSuccess', handlePaymentsRefresh);
    };
  }, [fetchData, loadPaymentTransactionDetail, useParentData]);

  const handlePaymentViewDetails = (payment) => {
    setOpenPaymentMenuKey(null);

    if (typeof onPaymentViewDetails === 'function') {
      onPaymentViewDetails(payment);
      return;
    }
    setIsPaymentDetailOpen(true);
    setSelectedPayment(payment);
    setFetchedPaymentDetail(null);
    loadPaymentTransactionDetail(payment);
  };

  const closePaymentDetail = () => {
    setIsPaymentDetailOpen(false);
    setSelectedPayment(null);
    setFetchedPaymentDetail(null);
    setPaymentDetailLoading(false);
  };

  const paymentForSheet = fetchedPaymentDetail || selectedPayment;

  return (
    <>
      <div
        className={`${styles.paymentDetails} ${styles.sidebarContainer}`}
        ref={showPaymentActions ? paymentMenuRef : null}
      >
        {displayLoading ? (
          <div className={styles.loading}>Loading...</div>
        ) : sortedVisibleDetails.length > 0 ? (
          sortedVisibleDetails.map((payment, index) => {
            if (!payment?.payment_method) {
              return null;
            }
            const key = String(
              getPaymentTransactionId(payment) ??
                `${payment.payment_method}-${payment.amount_value}-${index}`
            );
            const showRowMenu = showPaymentActions;
            const isMembershipPayment = payment?.payment_method_slug?.toLowerCase() === "other_membership" || payment?.payment_method_slug?.toLowerCase() === "cashless";
            const skipPasswordReason = payment?.skip_password_reason !== null && payment?.skip_password_reason !== '';
            const memberName = payment?.member_name?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
            const hasWebDeposit = payment?.panel?.toLowerCase() === "customer";
            const amountValue = String(payment.amount_value ?? '0');
            const normalizedAmount = amountValue.startsWith('-') ? amountValue.slice(1) : amountValue;
            const isRefundRow = isRefundPaymentRow(payment);

            const renderPaymentRow = ({ rowKey, isRefundRow: isSyntheticRefundRow = false }) => {
              const rowIsRefund = isSyntheticRefundRow || isRefundRow;
              const rowDetailOverlay = buildPaymentDetailOverlay(payment, { isRefundRow: rowIsRefund });
              const paymentMethodLabel = rowIsRefund
                ? `${payment.payment_method} (Refund)`
                : refundMode
                  ? `Refund (${payment.payment_method})`
                  : payment.payment_method;
              const closingBalance = parseAmount(payment?.balance_after);
              const referenceText = payment?.original_reference || (rowIsRefund ? payment?.payment_reference : null);

              return (
              <div
                key={rowKey}
                className={`${styles.rw} ${showRowMenu ? styles.rwWithMenu : ''}`}
              >
                <div className={`${styles.cl} ${styles.left}`}>
                  <div className={styles.methodWrap}>
                    {hasWebDeposit ? (
                      <div className={styles.webDepositInfo}>
                        Online Deposit Amount
                      </div>
                    ) : 
                    <div className={styles.paymentMethodHover}>
                      <div className={styles.paymentMethodName} title={memberName}>
                        {paymentMethodLabel}
                        {payment?.membership_number &&
                        ["cashless", "other_membership"].includes(payment?.payment_method_slug) ? (
                          <span>
                            (#{payment.membership_number})
                          </span>
                        ) : null}
                      </div>
                      {!hideMembershipPaymentMeta && isMembershipPayment && closingBalance !== null && (
                        <div className={`${styles.referenceText} ${styles.membershipInfoText}`}>
                          Closing Balance: ${formatAmount(closingBalance)}
                        </div>
                      )}
                    </div>
                    }
                    {referenceText && !rowIsRefund && !refundMode && (
                      <div className={styles.referenceText}>
                        <i>{referenceText}</i>
                      </div>
                    )}
                    {(!hideMembershipPaymentMeta && isMembershipPayment && skipPasswordReason) && (
                      <div className={styles.referenceText}>
                        <LockIcon size={18} color="var(--neutral-500)" /> By {payment?.created_by_name}
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.paymentRightGroup}>
                  {showRowMenu ? (
                    <div className={styles.paymentActionWrap}>
                      <button
                        type="button"
                        className={styles.paymentDotsButton}
                        aria-label="Payment options"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenPaymentMenuKey((prev) => (prev === rowKey ? null : rowKey));
                        }}
                      >
                        <img src={menuIcon} alt="" className={styles.paymentMenuIcon} />
                      </button>
                      {openPaymentMenuKey === rowKey ? (
                        <ul className={styles.paymentMenuDropdown}>
                          <li
                            className={styles.paymentMenuItem}
                            onClick={() => handlePaymentViewDetails({
                              ...payment,
                              ...rowDetailOverlay,
                            })}
                          >
                            View Details
                          </li>
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                  <div className={`${styles.cl} ${styles.right}`}>
                    {(refundMode || rowIsRefund) && '-'}
                    {payment.amount_currency === 'SGD' && '$'}
                    {(refundMode || rowIsRefund) ? normalizedAmount : payment.amount_value}
                  </div>
                </div>
              </div>
              );
            };

            if (useLegacyRefundPair) {
              return [
                renderPaymentRow({ rowKey: `${key}-paid` }),
                renderPaymentRow({ rowKey: `${key}-refund`, isSyntheticRefundRow: true }),
              ];
            }

            return renderPaymentRow({ rowKey: key });
          })
        ) : (
          ''
        )}
      </div>
      {isPaymentDetailOpen && paymentForSheet ? (
        <MembershipTxnPaymentDetailSheet
          paymentTransactionDetail={paymentForSheet}
          allPayments={displayDetails}
          saleId={saleIdProp ?? bookingDetails?.sale_id ?? null}
          selectedPayment={paymentForSheet}
          loading={paymentDetailLoading}
          stackWidth="min(400px, 100vw)"
          refundDate={paymentForSheet?.updated_at ?? bookingDetails?.updated_at}
          onClose={closePaymentDetail}
          onGoToSale={onGoToSale}
        />
      ) : null}
    </>
  );
};

export default BookingPaymentDetails;
